import { Component, ChangeDetectionStrategy, inject, OnInit } from "@angular/core";
import { Router } from "@angular/router";
import { QuestionnaireService } from "../../services/questionnaire.service";
import { SnackbarService } from "../../services/snackbar.service";
import { Questionnaire } from "../../classes/flow/Questionnaire/Questionnaire";
import { FormControl, FormGroup, Validators, FormsModule, ReactiveFormsModule } from "@angular/forms";
import { TranslateService, TranslatePipe } from "@ngx-translate/core";
import { specialCharValidator } from "../../validators/specialChars";
import { QuestionnaireFormComponent } from "../../components/questionnaire-form/questionnaire-form.component";
import { MatButton } from "@angular/material/button";
import { EpfService } from "src/app/services/epf.service";
import { ApplicationService } from "src/app/services/application.service";
import { QuestionnaireType } from "src/app/classes/flow/Questionnaire/QuestionnaireType";
import { Specialty } from "src/app/classes/flow/Questionnaire/Specialty";

@Component({
  selector: "app-new-questionnaire",
  templateUrl: "./new-questionnaire.component.html",
  styleUrls: ["./new-questionnaire.component.less"],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [QuestionnaireFormComponent, FormsModule, ReactiveFormsModule, MatButton, TranslatePipe],
})
export class NewQuestionnaireComponent implements OnInit {
  private readonly translateService = inject(TranslateService);
  private readonly questionnaireService = inject(QuestionnaireService);
  private readonly snackbarService = inject(SnackbarService);
  private readonly application = inject(ApplicationService);
  private readonly router = inject(Router);
  private epf = inject(EpfService);

  public specialties: Specialty[] = [];
  public questionnaireTypes: QuestionnaireType[] = [];

  public questionnaireForm: FormGroup = new FormGroup({
    specialties: new FormControl([], Validators.required),
    title: new FormControl("", [Validators.required, specialCharValidator]),
    description: new FormControl("", [Validators.required, specialCharValidator]),
    questionnaireType: new FormControl("", Validators.required),
  });

  async ngOnInit(): Promise<void> {
    this.application.setLoading(true);
    const [{ specialties }, { questionnaireTypes }] = await Promise.all([this.epf.start(), this.epf.metadata()]);
    this.specialties = specialties;
    this.questionnaireTypes = questionnaireTypes;
    this.application.setLoading(false);
  }

  /**
   * Saves the user input as a new questionnaire
   */
  public async saveQuestionnaire() {
    if (this.questionnaireForm.valid) {
      try {
        await this.epf.addQuestionnaire(this.makeQuestionnaire());
        this.router.navigate(["content/coordinator/all-questionnaires"]);
      } catch (error) {
        this.snackbarService.open(this.translateService.instant("FORMS.NEW_QUESTIONNAIRE.FAILURE_MESSAGE"));
      }
    }
  }

  /**
   * Builds a questionnaire from the individual input values
   * @returns The questionnaire based on the user's input
   * The .replace is needed due to the user being able to cause grapqhl errors with certain special characters
   */
  private makeQuestionnaire(): Questionnaire {
    const data = this.questionnaireForm.value;
    return {
      active: false,
      title: data.title,
      description: data.description,
      questionnaireType: data.questionnaireType,
      specialties: data.specialties,
      sortOrder: null,
    };
  }
}
